import { useState, useEffect, useRef } from 'react';
import { useStore } from '../../../../../../hooks';

// Status message mapping - extracted to avoid recreation
const STATUS_MESSAGES = {
  preparing: 'Preparing file...',
  uploading: 'Uploading file...',
  creating: 'Creating document entry...',
  default: 'Processing file...',
};

/**
 * Hook for file upload state management
 * Observes documentsStore to track upload progress and provides transformed data
 * Note: The component using this hook must be wrapped with observer() for MobX reactivity
 */
export const useUploadFiles = () => {
  const { documentsStore } = useStore();
  const [simulatedProgress, setSimulatedProgress] = useState({});
  const intervalsRef = useRef({});

  // Simulate progress for files in processing state
  useEffect(() => {
    // Access observables directly so MobX can track them
    const tempDocs = documentsStore.temporaryDocuments;
    const uploadProgress = documentsStore.uploadProgress;
    
    // Track which files need simulation
    const filesToSimulate = new Set();
    
    tempDocs.forEach((doc) => {
      const fileId = doc.uploadId || doc.id;
      const progress = uploadProgress[doc.name] || {};
      const progressPercentage = progress.percentage || 0;
      
      // Only simulate if state is 'loading' (processing) and progress < 100
      if (doc.state === 'loading' && progressPercentage < 100) {
        filesToSimulate.add(fileId);
        
        // Start simulation if not already running
        if (!intervalsRef.current[fileId]) {
          let currentProgress = progressPercentage;
          
          intervalsRef.current[fileId] = setInterval(() => {
            currentProgress += 2; // Increment by 2% each interval
            
            setSimulatedProgress((prev) => {
              const newValue = Math.min(currentProgress, 100);
              // Only update if value changed to avoid unnecessary re-renders
              if (prev[fileId] !== newValue) {
                return {
                  ...prev,
                  [fileId]: newValue,
                };
              }
              return prev;
            });
            
            // When reaching 100%, stop simulation
            if (currentProgress >= 100) {
              clearInterval(intervalsRef.current[fileId]);
              delete intervalsRef.current[fileId];
            }
          }, 100); // Update every 100ms
        }
      }
    });

    // Clean up intervals for files no longer in loading state
    Object.keys(intervalsRef.current).forEach((fileId) => {
      if (!filesToSimulate.has(fileId)) {
        clearInterval(intervalsRef.current[fileId]);
        delete intervalsRef.current[fileId];
      }
    });

    // Cleanup function
    return () => {
      Object.values(intervalsRef.current).forEach(interval => {
        clearInterval(interval);
      });
      intervalsRef.current = {};
    };
  }, [documentsStore.temporaryDocuments, documentsStore.uploadProgress]);

  // Transform temporaryDocuments to UploadStatus format
  // Access observables directly - observer will handle reactivity
  // This will re-run when documentsStore.temporaryDocuments or documentsStore.uploadProgress change
  const uploadFiles = documentsStore.temporaryDocuments.map((doc) => {
    const progress = documentsStore.uploadProgress[doc.name] || {};
    const fileId = doc.uploadId || doc.id;
    
    // Use simulated progress if available and file is in processing state
    const simulatedProgressValue = simulatedProgress[fileId];
    const progressPercentage = doc.state === 'loading' && simulatedProgressValue !== undefined
      ? simulatedProgressValue
      : progress.percentage || 0;
    
    // Map store states to UploadStatus status prop
    let status = 'processing';
    let statusMessage = '';
    
    if (doc.state === 'loading') {
      // If simulated progress reached 100%, mark as done
      if (simulatedProgressValue !== undefined && simulatedProgressValue >= 100) {
        status = 'done';
        statusMessage = '';
      } else if (progressPercentage >= 100) {
        // Also check if real progress reached 100%
        status = 'done';
        statusMessage = '';
      } else {
        status = 'processing';
        // Map progress status to status message
        statusMessage = STATUS_MESSAGES[progress.status] || STATUS_MESSAGES.default;
      }
    } else if (doc.state === 'error') {
      status = 'error';
      statusMessage = progress.errorMessage || 'Upload failed';
    } else if (doc.state === 'active') {
      status = 'done';
      statusMessage = '';
    }

    // When status is 'done', progress must be 100
    const finalProgress = status === 'done' ? 100 : Math.min(progressPercentage, 100);

    return {
      id: fileId,
      name: doc.name,
      progress: finalProgress,
      status,
      statusMessage,
      uploadedAt: doc.createdAt ? new Date(doc.createdAt) : new Date(),
      lastUpdatedAt: progress.lastUpdated ? new Date(progress.lastUpdated) : new Date(),
      file: doc.file,
      retryOptions: doc.retryOptions,
    };
  });

  return {
    uploadFiles,
    isUploading: documentsStore.isUploading,
    hasUploads: uploadFiles.length > 0,
    documentsStore, // Expose store for retry functionality
  };
};

